import React, { useEffect, useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import lessons from "../data/lessons";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

const levelColor = {
  seeker: "#12bfbe",
  learner: "#9198e5",
  skilled_helper: "#1e3a8a",
  community_helper: "#16a34a",
};

const LearningCenter = () => {
  useDocTitle("ZelT | ZelPath — Learning Center");
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(null);
  const [openLesson, setOpenLesson] = useState(null);
  const [path, setPath] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("zelt_token")) {
      navigate("/membership");
      return;
    }
    axios
      .get(`${API_BASE}/my-progress`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` },
      })
      .then((r) => setCompleted(r.data.completed))
      .catch(() => setCompleted([]));
    axios
      .get(`${API_BASE}/my-path`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` },
      })
      .then((r) => setPath(r.data))
      .catch(() => {});
  }, [navigate]);

  const markComplete = (lessonId) => {
    axios
      .post(
        `${API_BASE}/my-progress`,
        { lessonId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` } }
      )
      .then((r) => setCompleted(r.data.completed))
      .catch(() => {});
  };

  const isDone = (id) => completed?.includes(id);
  const doneCount = completed?.length || 0;

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-3xl mx-auto">
          <div className="text-center mb-10" data-aos="zoom-in">
            <span className="text-5xl">🎓</span>
            <h1 className="text-4xl font-extrabold text-blue-900 mt-3 mb-3">
              ZelPath Learning Center
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Short, practical lessons — no jargon, no lectures. Complete them
              and build your Technology Health.
            </p>

            {/* Technology Health meter */}
            {completed !== null && (
              <div className="mt-6 max-w-md mx-auto bg-gray-50 rounded-2xl p-5 shadow-md">
                <p className="text-sm font-bold text-blue-900 mb-2">
                  Your Technology Health
                </p>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.round((doneCount / lessons.length) * 100)}%`,
                      background: "linear-gradient(90deg, #12bfbe, #9198e5)",
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {doneCount} of {lessons.length} lessons complete
                  {doneCount === lessons.length && " — full health! 🏆"}
                </p>
              </div>
            )}
          </div>

          {/* ZelPath progression panel */}
          {path && (
            <div
              className="max-w-md mx-auto mb-10 rounded-2xl p-5 text-white shadow-lg"
              style={{
                background: `linear-gradient(135deg, #0f172a, ${levelColor[path.level] || "#1e3a8a"})`,
              }}
              data-aos="fade-up"
            >
              <p className="text-sm font-bold text-teal-300 mb-1">🛤️ Your ZelPath</p>
              <p className="text-2xl font-extrabold mb-3">{path.levelLabel}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {path.badges.map((b) => (
                  <span
                    key={b.id}
                    className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-bold text-white"
                  >
                    {b.icon} {b.name}
                  </span>
                ))}
              </div>
              {path.volunteerEligible && (
                <div className="bg-white/10 rounded-xl p-4 text-left">
                  <p className="font-bold text-teal-300 text-sm mb-1">
                    🤝 You're ready to become a Community Helper!
                  </p>
                  <p className="text-gray-300 text-xs mb-3">
                    All lessons complete. Apply to volunteer — verified members
                    like you can be assigned real help cases from the
                    community.
                  </p>
                  <Link
                    to="/contact?request=volunteer"
                    className="inline-block px-4 py-2 rounded-lg bg-teal-400 text-blue-900 text-xs font-bold hover:bg-teal-300 transition"
                  >
                    Apply to volunteer →
                  </Link>
                </div>
              )}
              {path.level === "skilled_helper" && path.isVolunteer && (
                <p className="text-teal-300 text-sm font-semibold">
                  ✅ You're a Community Helper — assigned cases appear in your
                  Staff Workspace.
                </p>
              )}
            </div>
          )}

          {/* lessons */}
          <div className="space-y-5">
            {lessons.map((l) => {
              const done = isDone(l.id);
              return (
                <div
                  key={l.id}
                  className={`rounded-2xl shadow-md border-l-4 overflow-hidden ${
                    done ? "bg-green-50 border-green-400" : "bg-gray-50 border-teal-400"
                  }`}
                  data-aos="fade-up"
                >
                  <button
                    onClick={() => setOpenLesson(openLesson === l.id ? null : l.id)}
                    className="w-full p-5 flex items-center gap-4 text-left"
                  >
                    <span className="text-3xl">{l.icon}</span>
                    <div className="flex-1">
                      <p className="font-extrabold text-blue-900">
                        {l.title}{" "}
                        {done && <span className="text-green-600 text-sm font-bold ml-1">✓ Completed</span>}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {l.minutes} min read
                      </p>
                    </div>
                    <span className="text-teal-500 font-bold text-xl">
                      {openLesson === l.id ? "−" : "+"}
                    </span>
                  </button>
                  {openLesson === l.id && (
                    <div className="px-5 pb-5 border-t border-gray-200 pt-4">
                      {l.body.map((section, i) => (
                        <div key={i} className="mb-4">
                          <h3 className="font-bold text-blue-900 mb-1 text-sm uppercase tracking-wide">
                            {section.heading}
                          </h3>
                          {section.text && (
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {section.text}
                            </p>
                          )}
                          {section.list && (
                            <ul className="space-y-1.5">
                              {section.list.map((item) => (
                                <li
                                  key={item}
                                  className="flex gap-2 text-gray-600 text-sm"
                                >
                                  <span className="text-teal-500 font-bold">•</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => markComplete(l.id)}
                        disabled={done}
                        className={`mt-2 px-5 py-2 rounded-xl text-sm font-bold transition ${
                          done
                            ? "bg-green-100 text-green-700 cursor-default"
                            : "bg-blue-900 text-white hover:bg-blue-800"
                        }`}
                      >
                        {done ? "✓ Completed" : "Mark as Complete"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-400 text-xs mt-8">
            Your progress is saved to your member account. More lessons coming —
            AI, Wi-Fi security, privacy, and more.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LearningCenter;
